// useBadgeCounts — the one shared hook every badge-bearing surface (sidebar
// rows, the collapsed icon-rail overlay, the user-menu total) reads from
// (#983, Epic #956 phase 2a).
//
// Thin wrapper around mcp.getMyBadgeCounts() (public.get_my_badge_counts(),
// #960) using the same useMcp()/useRealtimeRefresh() pair every other
// realtime-backed page in this tree already uses (see
// src/pages/actions/index.tsx) — no hand-rolled fetch/loading state here.
//
// Realtime: refetches whenever ops.notifications changes (the channel #958
// added). This intentionally does NOT also subscribe to ops.actions — the
// actions-count half of the payload (badge_source_actions) is real-time-ish
// only in the sense that it reflects whatever a later page load sees;
// wiring a live ops.actions refresh into every badge consumer is out of
// this phase's scope (the issue's own "Out of scope" section reserves
// pipeline/to-do-page work for Phase 3).
//
// This is the one surface that opts into useRealtimeRefresh's
// `refreshOnSubscribe` catch-up (#1040): a badge that silently misses a
// notification until the next one arrives is exactly the failure a badge is
// supposed to prevent, and it is cheap to close here (one extra RPC per
// Layout mount) because Layout is the hook's sole caller (#1019).
//
// `countFor(url)` is the shape every consumer actually wants: AppSidebar's
// NavItem.url values ARE the surface keys get_my_badge_counts() returns
// (see that migration's header), so a sidebar row looks up its own count by
// its own url with no extra mapping table. A surface the caller cannot
// reach is simply absent from `surfaces` (never present at 0) — this
// returns 0 for it either way, which renders as "no badge" (see Badge.tsx's
// CountBadge, which hides at count <= 0). The component must never
// re-derive surface visibility itself; it only ever asks "what count does
// the server say I have for this surface", which is already gated.

import { useCallback, useState } from "react";
import { mcp, useMcp } from "@/lib/mcp";
import {
  useRealtimeRefresh,
  type RealtimeStatus,
} from "@/hooks/useRealtimeRefresh";

export interface BadgeSurfaceCounts {
  count: number;
  kinds: Record<string, number>;
}

export interface UseBadgeCountsResult {
  /** Sum of every surface's count the caller can see (never a shown-zero). */
  total: number;
  /** Raw surfaces map, keyed by nav url — see get_my_badge_counts()'s shape. */
  surfaces: Record<string, BadgeSurfaceCounts>;
  loading: boolean;
  error: string | null;
  /** Exact count for one nav url, or 0 when absent/not-yet-loaded. */
  countFor: (url: string) => number;
  /** Manual refetch — components don't normally need this; the realtime
   *  subscription already refetches on every ops.notifications change. */
  refresh: () => void;
  /**
   * Connection state of the ops.notifications channel: "connecting" until
   * the websocket has joined, then "subscribed" / "error" (#1040). Counts
   * are already correct before this reaches "subscribed" — the initial RPC
   * doesn't wait on the socket — what it tells you is whether a change
   * landing RIGHT NOW would arrive without a reload. Layout surfaces it as
   * a hidden marker so tests/e2e/badge-counts.spec.ts can wait for the
   * channel to be live before emitting the notification it asserts on.
   */
  realtimeStatus: RealtimeStatus;
}

const EMPTY_SURFACES: Record<string, BadgeSurfaceCounts> = {};

export function useBadgeCounts(): UseBadgeCountsResult {
  const { data, loading, error, refresh } = useMcp(
    () => mcp.getMyBadgeCounts(),
    [],
  );

  const [realtimeStatus, setRealtimeStatus] =
    useState<RealtimeStatus>("connecting");

  // Stable callback identity isn't required — useRealtimeRefresh stashes the
  // latest callback in a ref and only re-subscribes when the binding list
  // changes (see that hook's own comment), so a fresh `refresh` on every
  // render is fine here, same as every other useMcp + useRealtimeRefresh
  // pairing in this codebase.
  useRealtimeRefresh(
    [{ schema: "ops", table: "notifications" }],
    useCallback(() => refresh(), [refresh]),
    {
      // Refetch the moment the channel is live, and again after any
      // reconnect: postgres_changes has no replay, so a notification that
      // lands while the socket is still connecting (or during a drop) is
      // never delivered, and the badge would stay stale until the next
      // notification or a page reload. See "THE NO-REPLAY WINDOW" in
      // useRealtimeRefresh.ts — this was #1040's root cause.
      refreshOnSubscribe: true,
      onStatusChange: setRealtimeStatus,
    },
  );

  const surfaces = data?.surfaces ?? EMPTY_SURFACES;

  return {
    total: data?.total ?? 0,
    surfaces,
    loading,
    error: error?.message ?? null,
    countFor: (url: string) => surfaces[url]?.count ?? 0,
    refresh,
    realtimeStatus,
  };
}
